import { gtmViewAffiliateSection } from '@/utils/GTM';
import { type MutableRefObject, useEffect, useRef } from 'react';

export interface ProductTrackingData {
  productAsin: string;
  productTitle: string;
  linkUrl: string;
  productBadge: string;
}

export interface UseAffiliateSectionTrackingOptions {
  onImpression?: () => void;
  threshold?: number;
  productIds?: string[];
}

const SECTION_STORAGE_KEY = 'affiliate_section_impression';

const getImpressedSections = (): string[] => {
  if (typeof window === 'undefined') return [];
  try {
    const stored = sessionStorage.getItem(SECTION_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const hasBeenImpressed = (productIds: string[]): boolean => {
  const impressedProducts = getImpressedSections();
  return productIds.every((id) => impressedProducts.includes(id));
};

const markAsImpressed = (productIds: string[]): void => {
  if (typeof window === 'undefined') return;

  const impressedProducts = getImpressedSections();

  productIds.forEach((id) => {
    if (!impressedProducts.includes(id)) {
      impressedProducts.push(id);
    }
  });

  sessionStorage.setItem(SECTION_STORAGE_KEY, JSON.stringify(impressedProducts));
};

export const useAffiliateSectionTracking = (options: UseAffiliateSectionTrackingOptions = {}) => {
  const { threshold = 0.5, onImpression, productIds } = options;
  const sectionRef = useRef<HTMLDivElement>(null) as MutableRefObject<HTMLDivElement | null>;
  const hasTrackedImpression = useRef(false);

  useEffect(() => {
    const element = sectionRef.current;
    if (!element) return;

    if (!productIds || productIds?.length === 0) {
      return;
    }

    // Check if already impressed in this session
    if (hasBeenImpressed(productIds)) {
      hasTrackedImpression.current = true;
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasTrackedImpression.current) {
            hasTrackedImpression.current = true;
            markAsImpressed(productIds);

            // Track section impression event
            gtmViewAffiliateSection();

            if (onImpression) {
              onImpression();
            }

            observer.unobserve(element);
          }
        });
      },
      { threshold },
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
      hasTrackedImpression.current = false;
    };
  }, [sectionRef.current, threshold, onImpression]);

  return { sectionRef };
};
